import {
  HeroMinimal,
  PipelineDiagram,
  PipelineStats,
  BookingCTA,
} from "../components/sections";
import { SectionHeader, Divider, CTA } from "../components/sections";
import { Users, Brain, Zap, CheckCircle, BarChart3 } from "lucide-react";

const pipelineStepsDetail = [
  {
    number: "01",
    label: "Define",
    title: "Define ICP & Strategy",
    description:
      "Define ideal customer profile, target market, buyer personas, segmentation, goals, and sales objectives.",
    inputs: [
      "Product positioning",
      "Current customer data",
      "Market research",
      "Revenue targets",
      "Sales process documentation",
    ],
    process:
      "We conduct structured discovery to codify your ICP, map buyer committees, define segmentation criteria, and establish success metrics. This becomes the system's north star.",
    aiRole:
      "Pattern analysis on historical wins/losses; lookalike modeling; market sizing validation.",
    humanRole:
      "Strategic decisions on positioning, pricing, market prioritization; approval of ICP definition.",
    outputs: [
      "Validated ICP documents",
      "Segmentation matrix",
      "Buyer persona profiles",
      "Success metrics & KPIs",
      "System configuration spec",
    ],
  },
  {
    number: "02",
    label: "Discover",
    title: "Prospect Discovery",
    description:
      "Identify companies and prospects matching the target profile using multiple data sources and signals.",
    inputs: [
      "ICP definition",
      "Segmentation criteria",
      "Technographic requirements",
      "Geographic targets",
      "Company size ranges",
    ],
    process:
      "Multi-source company identification combining firmographic databases, technographic signals, intent data, hiring signals, funding events, and lookalike modeling against your best customers.",
    aiRole:
      "Lookalike scoring; intent signal classification; predictive fit modeling; anomaly detection in data sources.",
    humanRole:
      "Source selection; quality thresholds; exclusion criteria; strategic account prioritization.",
    outputs: [
      "Qualified company universe",
      "Prioritized account lists",
      "Intent-scored prospects",
      "Expansion recommendations",
    ],
  },
  {
    number: "03",
    label: "Enrich & Verify",
    title: "Data Enrichment & Verification",
    description:
      "Collect relevant data, enrich records, validate contacts, and improve data quality across all touchpoints.",
    inputs: [
      "Company universe",
      "Contact requirements",
      "Data quality thresholds",
      "Verification standards",
    ],
    process:
      "Multi-source enrichment (firmographics, technographics, contact data, social profiles) followed by real-time email/phone verification, deliverability scoring, and continuous data freshness monitoring.",
    aiRole:
      "Data fusion across sources; conflict resolution; anomaly detection; predictive bounce scoring.",
    humanRole:
      "Source vendor management; quality threshold calibration; exception handling for strategic accounts.",
    outputs: [
      "Enriched contact records",
      "Verified email/phone data",
      "Data quality scores",
      "Deliverability predictions",
    ],
  },
  {
    number: "04",
    label: "Build",
    title: "Campaign Architecture",
    description:
      "Create segmentation, research context, personalization logic, messaging, and campaign structure.",
    inputs: [
      "Enriched prospect data",
      "ICP & personas",
      "Value propositions",
      "Competitive positioning",
      "Compliance requirements",
    ],
    process:
      "Design micro-segments, build AI research prompts per segment, create personalization rules engines, develop message frameworks, structure multi-touch sequences, and plan A/B test hypotheses.",
    aiRole:
      "Research prompt generation; personalization rule synthesis; message variant generation; sequence optimization.",
    humanRole:
      "Messaging strategy; brand voice; compliance review; sequence logic approval; test hypothesis design.",
    outputs: [
      "Segment definitions",
      "Research configurations",
      "Personalization engines",
      "Message frameworks",
      "Sequence blueprints",
      "Test plans",
    ],
  },
  {
    number: "05",
    label: "Reach",
    title: "Outbound Execution",
    description:
      "Deploy outbound workflows, personalized messaging, follow-ups, and response handling at scale.",
    inputs: [
      "Campaign architecture",
      "Personalized content",
      "Sending infrastructure",
      "CRM integration",
      "Compliance rules",
    ],
    process:
      "Execute multi-channel sequences (email, LinkedIn, phone) with smart throttling, timezone optimization, behavioral triggers, automated follow-ups, response classification, and real-time CRM synchronization.",
    aiRole:
      "Send-time optimization; response classification; next-best-action decisions; anomaly detection.",
    humanRole:
      "Strategic oversight; escalation handling; relationship management for warm responses; quality spot-checks.",
    outputs: [
      "Delivered outreach",
      "Engagement data",
      "Response classifications",
      "CRM activities",
      "Meeting bookings",
    ],
  },
  {
    number: "06",
    label: "Optimize",
    title: "Continuous Optimization",
    description:
      "Monitor performance, qualify responses, learn from outcomes, and continuously improve the system.",
    inputs: [
      "Performance data",
      "Response data",
      "Meeting outcomes",
      "Pipeline metrics",
      "Revenue attribution",
    ],
    process:
      "Analyze cohort performance, run statistical significance tests on A/B experiments, retrain scoring models, refine segmentation, update personalization rules, and iterate sequence architecture based on closed-loop feedback.",
    aiRole:
      "Statistical analysis; model retraining; pattern detection; predictive optimization recommendations.",
    humanRole:
      "Strategic interpretation; resource allocation decisions; hypothesis generation; stakeholder reporting.",
    outputs: [
      "Optimization reports",
      "Updated models",
      "Refined segments",
      "Improved sequences",
      "Strategic recommendations",
    ],
  },
];

function HowItWorksPage() {
  return (
    <>
      <HeroMinimal
        title="How an Outbound Sales System Actually Works"
        subtitle="A clear process for finding the right buyers, making outreach relevant, and turning responses into useful sales conversations."
        ctaText="Book a Strategy Call"
        ctaHref="/book"
        secondaryCtaText="Explore Solutions"
        secondaryCtaHref="/solutions"
      />

      {/* Pipeline Overview */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="pipeline-overview-title"
      >
        <div className="container">
          <SectionHeader
            id="pipeline-overview-title"
            eyebrow="System Overview"
            title="The Complete Pipeline"
            description="Six stages. Continuous loop. Each iteration makes the system smarter and more effective."
            align="center"
          />
          <PipelineDiagram showDetails />
          <PipelineStats />
        </div>
      </section>

      {/* Detailed Stages */}
      <section className="section" aria-labelledby="stages-title">
        <div className="container">
          <SectionHeader
            id="stages-title"
            eyebrow="Deep Dive"
            title="Every Stage Expanded"
            description="Inputs, process, AI involvement, human involvement, and outputs for each stage of the outbound engine."
            align="center"
          />
          <div className="max-w-5xl mx-auto mt-12 space-y-8">
            {pipelineStepsDetail.map((stage, index) => (
              <article
                key={stage.number}
                className="card-elevated p-8 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 text-accent font-bold text-xl">
                    {stage.number}
                  </div>
                  <div>
                    <span className="label text-accent mb-2 inline-block">
                      {stage.label}
                    </span>
                    <h2 className="h2 text-text-primary">{stage.title}</h2>
                    <p className="body text-text-secondary mt-1">
                      {stage.description}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                  <div className="md:col-span-1 lg:col-span-2">
                    <h4 className="label text-text-primary mb-3">Inputs</h4>
                    <ul className="space-y-1.5" role="list">
                      {stage.inputs.map((input, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-sm text-text-secondary"
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-accent"
                            aria-hidden="true"
                          />
                          {input}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="md:col-span-1 lg:col-span-3">
                    <h4 className="label text-text-primary mb-3">
                      What Happens
                    </h4>
                    <p className="body text-text-secondary mb-4">
                      {stage.process}
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-surface rounded-lg border border-border">
                        <h5 className="label text-accent mb-2 flex items-center gap-2">
                          <Brain size={14} aria-hidden="true" /> AI Role
                        </h5>
                        <p className="text-sm text-text-secondary">
                          {stage.aiRole}
                        </p>
                      </div>
                      <div className="p-4 bg-surface rounded-lg border border-border">
                        <h5 className="label text-blue-400 mb-2 flex items-center gap-2">
                          <Users size={14} aria-hidden="true" /> Human Role
                        </h5>
                        <p className="text-sm text-text-secondary">
                          {stage.humanRole}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-1 lg:col-span-2">
                    <h4 className="label text-text-primary mb-3">Outputs</h4>
                    <ul className="space-y-1.5" role="list">
                      {stage.outputs.map((output, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-sm text-text-secondary"
                        >
                          <CheckCircle
                            size={14}
                            className="text-accent flex-shrink-0"
                            aria-hidden="true"
                          />
                          {output}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="md:col-span-1 lg:col-span-3" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Continuous Loop */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="loop-title"
      >
        <div className="container">
          <SectionHeader
            id="loop-title"
            eyebrow="Continuous Improvement"
            title="The Optimization Loop"
            description="The system doesn't just execute. It learns. Every response, meeting, and deal feeds back into better targeting, messaging, and qualification."
            align="center"
          />
          <div className="lifecycle-grid">
            {[
              {
                step: "Analyze",
                description:
                  "Review replies, conversations, and pipeline against the agreed goals.",
                icon: BarChart3,
              },
              {
                step: "Learn",
                description:
                  "Identify useful patterns in targeting, messaging, and buyer response.",
                icon: Brain,
              },
              {
                step: "Refine",
                description:
                  "Adjust segments, research, personalization, and follow-up logic.",
                icon: Zap,
              },
              {
                step: "Scale",
                description:
                  "Expand the parts that work, with quality and infrastructure checks.",
                icon: Users,
              },
            ].map((item, index) => (
              <div key={item.step} className="lifecycle-card card">
                <span className="step-number">0{index + 1}</span>
                <h3 className="h3 flex items-center gap-3">
                  <item.icon
                    size={21}
                    className="text-accent"
                    aria-hidden="true"
                  />
                  {item.step}
                </h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Technical Depth Note */}
      <section className="section" aria-labelledby="technical-title">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <Divider />
            <h2
              id="technical-title"
              className="h1 md:display-sm text-text-primary mb-4"
            >
              Technical Depth Without Complexity
            </h2>
            <p className="body-lg text-text-secondary mb-8">
              This page is designed for technically minded Heads of Sales and
              Growth. The system architecture is sophisticated, but the
              interface for your team is simple: qualified prospects in your
              CRM, ready for conversation.
            </p>
            <CTA
              primaryText="Book a Technical Deep-Dive"
              primaryHref="/book"
              secondaryText="View AI & Automation"
              secondaryHref="/technology/ai-automation"
              align="center"
            />
          </div>
        </div>
      </section>

      <BookingCTA />
    </>
  );
}

export default HowItWorksPage;
